/**
 * ItemTile — Unified compact tile for equipment and fish items
 * Used in both EquipmentSection and FishInventorySection
 */

import type { ItemTileData } from './adapters';

interface ItemTileProps {
  data: ItemTileData;
  isSelected?: boolean;
  isCompatible?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
}

export function ItemTile({
  data,
  isSelected = false,
  isCompatible = false,
  isDisabled = false,
  onClick,
}: ItemTileProps) {
  const borderClass = isSelected
    ? 'ring-2 ring-blue-500 border-blue-500'
    : isCompatible
    ? 'ring-2 ring-green-500 border-green-500 animate-pulse'
    : 'border-gray-700 hover:border-gray-500';

  const opacityClass = isDisabled ? 'opacity-30 cursor-not-allowed' : '';

  return (
    <button
      type="button"
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      className={`w-full text-left p-2 rounded-lg border bg-gray-800/60 hover:bg-gray-800 transition-all ${borderClass} ${opacityClass}`}
    >
      {/* Row 1: Icon + Name */}
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-lg flex-shrink-0 leading-none">{data.icon}</span>
        <span className="text-sm font-bold text-white truncate">{data.name}</span>
      </div>

      {/* Row 2: Subtitle + Quantity + Badge */}
      <div className="flex items-center gap-1 mt-0.5 min-w-0">
        <span
          className="text-xs truncate"
          style={data.subtitleColor ? { color: data.subtitleColor } : undefined}
        >
          {data.subtitle}
        </span>
        {data.quantity !== undefined && (
          <span className="text-xs text-gray-400 flex-shrink-0">×{data.quantity}</span>
        )}
        {data.badge && (
          <span className={`text-xs ml-auto flex-shrink-0 ${data.badgeColor || 'text-gray-400'}`}>
            {data.badge}
          </span>
        )}
      </div>

      {/* Row 3: Stat chips */}
      {data.stats.length > 0 && (
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {data.stats.map((stat) => (
            <span
              key={stat.label}
              className={`text-xs ${stat.color || 'text-gray-400'}`}
            >
              {stat.label}{stat.value}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}
